import "../styles.scss";
import React, { useRef, useEffect, useState } from "react";
import { Message } from "./Message";
import { Control } from "./Control";
import { MESSAGE_ADDED_EVENT } from "../../../constants";
import { Listener, UnbindListener } from "../../../services/_";

const sanitize = (string) => {
  return string.replace(/\<(?!\/?(img|a|strong|b|i|strike)\b)[^\>]+\>/g, "");
};

export const linkify = (str) => {
  if (!str) return;

  const text = str.replace(
    /(?:https?\:\/\/|www\.)+(?![^\s]*?")([\w.,@?!^=%&amp;:\/~+#-]*[\w@?!^=%&amp;\/~+#-])?/gi,
    (url) => {
      let wrap = document.createElement("div");
      let anch = document.createElement("a");

      // URLs starting with http://, https://, or ftp://
      if (
        /(\b(https?|ftp):\/\/[-A-Z0-9+&#\/%?=~_|!:,.;]*[-A-Z0-9+&#\/%=~_|])/gim.test(
          url
        )
      ) {
        anch.href = url;
        // URLs starting with www
      } else if (/(^|[^\/])(www\.[\S]+(\b|$))/gim.test(url)) {
        anch.href = `http://${url}`;
      } else {
        anch.href = "";
      }

      anch.target = "_blank";
      anch.innerHTML = url;
      wrap.appendChild(anch);
      return wrap.innerHTML;
    }
  );

  if (!text) return "";

  return text;
};

const cleanMessage = (message) => {
  if (!message) return;

  return {
    ...message,
    content: linkify(sanitize(message.content)),
  };
};

const parseMessages = (messages) => {
  return messages.map((message) => ({
    ...cleanMessage(message),
    reply_to_message: cleanMessage(
      messages.find((m) => m.uuid === message.reply_to)
    ),
  }));
};

const Messages = ({ hosts, messages }) => {
  const conversationContainer = useRef();
  const inputRef = useRef();
  const [replyTo, setReplyTo] = useState(null);
  const [focusMessage, setFocusMessage] = useState(null);
  messages = parseMessages(messages);

  useEffect(() => {
    if (replyTo) {
      inputRef.current.focus();
    }
  }, [replyTo]);

  useEffect(() => {
    const container = conversationContainer.current;

    // Apply class when user hovers over message container
    const activeHoverClass = "is-hovered";
    const mouseEnterFn = () => container.classList.add(activeHoverClass);
    const mouseLeaveFn = () => container.classList.remove(activeHoverClass);
    container.addEventListener("mouseenter", mouseEnterFn);
    container.addEventListener("mouseleave", mouseLeaveFn);

    const scrollToBottomFn = () => {
      setTimeout(() => {
        // Check component is on the page
        if (!container) return;

        // Only scroll when user is NOT hovering over previous messages
        if (!container.classList.contains(activeHoverClass)) {
          container.scrollTop = container.scrollHeight;
        }
      }, 200);
    };
    // Call scrollToBottomFn when component first mounts
    scrollToBottomFn();
    // Call scrollToBottomFn when new message is added
    const listeners = [Listener(MESSAGE_ADDED_EVENT, scrollToBottomFn)];
    // On Destroy: Unbind all listeners
    return () => {
      UnbindListener(listeners);
      container.removeEventListener("mouseover", mouseEnterFn);
      container.removeEventListener("mouseout", mouseLeaveFn);
    };
  }, []);

  return (
    <div className="live-chat-container d-flex flex-column">
      <div
        ref={conversationContainer}
        className="live-chat-conversation d-flex flex-column flex-grow-1"
      >
        {messages.map((message, i) => (
          <Message
            {...{
              focus: focusMessage === message.uuid,
              setFocusMessage,
              message,
              isHost: message.is_host,
              isAdmin: message.is_admin,
              key: i,
              setReplyTo,
            }}
          />
        ))}
      </div>
      <Control
        {...{
          replyTo,
          setReplyTo,
          replyToMessage: messages.find((m) => m.uuid === replyTo),
          inputRef,
        }}
      />
    </div>
  );
};

export default Messages;
